import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateService } from '@app/core/services/state.service';
import { TranslateEditDialogComponent, TranslateEditDialogData } from '@app/modal/translate-edit-dialog/translate-edit-dialog.component';

@Component({
    selector: 'tt-checklist-item',
    templateUrl: './checklist-item.component.html',
    styleUrls: ['./checklist-item.component.css'],
})
export class ChecklistItemComponent implements OnInit {
    checklistItemKeyno;

    checklistKeyno;

    get?: ChecklistItemGet;

    dependencies: Dependency[] = [];

    dependency1Values: DependencyValue[] = [];

    dependency2Values: DependencyValue[] = [];

    elementTypes: ElementType[] = [];

    methodButtons: FormButton[] = [
        {
            id: 'translate',
            icon: 'far fa-language',
            type: 'primary',
            tooltip: 'translations',
            translateTooltip: true,
            onClick: () => this.openMethodTranslationDialog(),
        },
    ];

    expectedResultButtons: FormButton[] = [
        {
            id: 'translate',
            icon: 'far fa-language',
            type: 'primary',
            tooltip: 'translations',
            translateTooltip: true,
            onClick: () => this.openExpectedResultTranslationDialog(),
        },
    ];

    elementTypeButtons: FormButton[] = [{ id: 'goto', icon: 'far fa-chevron-right', type: 'primary', goto: 'checklistitemitems', gotoParms: () => this.checklistItemKeyno }];

    saving = false;

    deleting = false;

    constructor(private datatask: DataTaskService, private state: StateService, private modal: ModalService, private dialog: MatDialog) {
        this.checklistItemKeyno = this.state.getStateParams()['checklistitem_keyno'];
        this.checklistKeyno = this.state.getStateParams()['checklist_keyno'];
    }

    public async saveItem() {
        if (!!this.get) {
            try {
                this.saving = true;

                await this.datatask.Post(547, this.get);

                this.state.back();
            } finally {
                this.saving = false;
            }
        }
    }

    public async deleteItem() {
        if (!!this.get) {
            try {
                this.deleting = true;

                const deleteConfirmed = await this.modal.openConfirmDialog({ title: 'confirm_title', translateMessage: false, message: this.get?.deletewarning, type: 'danger' });

                if (deleteConfirmed) {
                    const response = await this.datatask.Post(854, { checklistitem_keyno: this.checklistItemKeyno });

                    if (response?.validation !== true) {
                        this.state.back();
                    }
                }
            } finally {
                this.deleting = false;
            }
        }
    }

    public async loadDependency1Values() {
        if (!!this.get) {
            if (this.get.checklistitem_keyno_dep !== '0') {
                this.dependency1Values = await this.getDependencyValues(this.get.checklistitem_keyno_dep);
            } else {
                this.get.checklistitem_depvalue = '';
                this.dependency1Values = [];
            }
        }
    }

    public async loadDependency2Values() {
        if (!!this.get) {
            if (this.get.checklistitem_keyno_dep2 !== '0') {
                this.dependency2Values = await this.getDependencyValues(this.get.checklistitem_keyno_dep2);
            } else {
                this.get.checklistitem_depvalue2 = '';
                this.dependency2Values = [];
            }
        }
    }

    private async loadDependencies() {
        if (!!this.checklistItemKeyno) {
            this.dependencies = await this.datatask.Post(545, { checklistitem_keyno: this.checklistItemKeyno });
        }
    }

    private async loadElementTypes() {
        this.elementTypes = await this.datatask.Post(544);
    }

    private async loadGet() {
        if (!!this.checklistItemKeyno && !!this.checklistKeyno) {
            this.get = (await this.datatask.Post(543, { checklistitem_keyno: this.checklistItemKeyno, checklist_keyno: this.checklistKeyno }))[0];
        }
    }

    private getDependencyValues(checklistItemKeyno: string): Promise<DependencyValue[]> {
        return this.datatask.Post(546, { checklistitem_keyno: checklistItemKeyno });
    }

    private openMethodTranslationDialog = async () => {
        if (!!this.get) {
            const edits: ChecklistItemLangEdit[] = await this.datatask.Post(3624, { checklistitem_keyno: this.get.checklistitem_keyno });
            const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
                width: '120rem',
                maxWidth: '95vw',
                height: '80vh',
                data: <TranslateEditDialogData<ChecklistItemLangEdit>>{
                    heading: `${this.get.method}`,
                    textToTranslate: this.get.method,
                    edits: edits,
                    save: async (data) => {
                        const response = await this.datatask.Post(3625, { edits: data.edits });

                        if (response?.validation !== true) {
                            await this.loadGet();
                            dialogRef.close();
                        }
                    },
                    wordNameKey: 'checklistitemlang_name',
                    wordIdKey: 'checklistitem_keyno',
                },
            });
        }
    };

    private openExpectedResultTranslationDialog = async () => {
        if (!!this.get) {
            const edits: ChecklistItemHelptextLangEdit[] = await this.datatask.Post(3626, { checklistitem_keyno: this.get.checklistitem_keyno });
            const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
                width: '120rem',
                maxWidth: '95vw',
                height: '80vh',
                data: <TranslateEditDialogData<ChecklistItemHelptextLangEdit>>{
                    heading: `${this.get.expected_result}`,
                    textToTranslate: this.get.expected_result,
                    edits: edits,
                    save: async (data) => {
                        await this.datatask.Post(3627, { edits: data.edits });
                        await this.loadGet();
                        dialogRef.close();
                    },
                    wordNameKey: 'checklistitemlang_helptext',
                    wordIdKey: 'checklistitem_keyno',
                },
            });
        }
    };

    ngOnInit(): void {
        this.loadGet();
        this.loadElementTypes();
        this.loadDependencies();
    }
}

interface DependencyValue {
    checklistitemitem_keyno: string;
    checklistitemitem_name: string;
    orderby: string;
}

interface Dependency {
    checklistitem_keyno: string;
    method: string;
    elementtype: string;
    orderby: string;
}

interface ElementType {
    elementtype: string;
    elementtype_name: string;
    orderby: string;
}

interface ChecklistItemGet {
    checklistitem_keyno: string;
    checklist_keyno: string;
    method: string;
    expected_result: string;
    elementtype: string;
    checklistitem_order: string;
    checklistitem_required: '1' | '0';
    checklistitem_keyno_dep: string;
    checklistitem_keyno_dep2: string;
    checklistitem_depvalue: string;
    checklistitem_depvalue2: string;
    depdisplay1: string;
    depdisplay2: string;
    deletewarning: string;
    isactive: '1' | '0';
    computevalidation: '1' | '0';
}

interface ChecklistItemLangEdit extends Record<string, string> {
    checklistitem_keyno: string;
    checklistitemlang_name: string;
    language_id: string;
    language_name: string;
    orderby: string;
    google_language_id: string;
}

interface ChecklistItemHelptextLangEdit extends Record<string, string> {
    checklistitem_keyno: string;
    checklistitemlang_helptext: string;
    language_id: string;
    language_name: string;
    orderby: string;
    google_language_id: string;
}
